// RTAB-Map's own feature matches (prep/features.py) as a check on a camera alignment it wasn't fitted to: a word
// that two frames close together (by id) share is mostly one point, so its keypoints should agree: the one frame's
// 3D point (its depth) seen by the other camera on the other's keypoint (in the photos), and the two 3D points
// in one place (in depth). Scored as medians, before and after a set of moves. Pure.
import { type Rigid, apply, compose, inverse } from '../align.ts';
import type { OrigFeatures } from '../format.ts';
import type { PoseGraph } from '../edit/springs.ts';
import { type PhotoCam, project } from './camgeom.ts';

/** Frames this close (by id) share a word as one point; further apart, often by chance. */
export const NEAR_FRAMES = 2;

export interface Keypoint { f: number; word: number; x: number; y: number; X: number; Y: number; Z: number }
export interface RtabPairs { a: Keypoint[]; b: Keypoint[]; w: number; h: number }

/** The keypoint pairs of near frames sharing a word (frames in keep only). */
export function rtabPairs(doc: OrigFeatures, keep: (f: number) => boolean): RtabPairs {
  const byWord = new Map<number, Keypoint[]>();
  for (const [f, word, x, y, X, Y, Z] of doc.kp) {
    if (!keep(f) || !(X || Y || Z)) continue;
    (byWord.get(word) ?? byWord.set(word, []).get(word)!).push({ f, word, x, y, X, Y, Z });
  }
  const a: Keypoint[] = [], b: Keypoint[] = [];
  for (const ks of byWord.values()) for (let i = 0; i < ks.length; i++) for (let j = i + 1; j < ks.length; j++)
    if (Math.abs(ks[i].f - ks[j].f) <= NEAR_FRAMES) { a.push(ks[i]); b.push(ks[j]); }
  return { a, b, w: doc.w, h: doc.h };
}

export interface RtabScore { n: number; px: number; mm: number }

/**
 * How well the pairs agree with the cameras as given (cam(f): frame f's camera, null: not shown) moved by moves
 * (world, per frame): median px off in the photos (both ways) and mm apart in depth. g: the pose graph (a keypoint's
 * point is in its frame's base frame: world = camera · camera pose⁻¹ · graph pose · point); camPose(f): the camera's
 * pose in the file frame (images.py).
 */
export function rtabScore(p: RtabPairs, cam: (f: number) => PhotoCam | null, camPose: (f: number) => Rigid | undefined, g: PoseGraph, moves?: Map<number, Rigid>): RtabScore | null {
  const base = new Map<number, Rigid | null>(), cams = new Map<number, PhotoCam | null>();
  const camOf = (f: number) => {
    if (!cams.has(f)) { const c = cam(f), W = moves?.get(f); cams.set(f, c && W ? { ...c, P: compose(W, c.P) } : c); }
    return cams.get(f)!;
  };
  const baseOf = (f: number) => {   // the frame's base frame → world, as its camera has it
    if (!base.has(f)) { const c = camOf(f), cp = camPose(f), gp = g.pose.get(f); base.set(f, c && cp && gp ? compose(c.P, compose(inverse(cp), gp)) : null); }
    return base.get(f)!;
  };
  const px: number[] = [], mm: number[] = [];
  for (let i = 0; i < p.a.length; i++) {
    const A = p.a[i], B = p.b[i], ba = baseOf(A.f), bb = baseOf(B.f), ca = camOf(A.f), cb = camOf(B.f);
    if (!ba || !bb || !ca || !cb) continue;
    const Xa = apply(ba, [A.X, A.Y, A.Z]), Xb = apply(bb, [B.X, B.Y, B.Z]);
    mm.push(1000 * Math.hypot(Xa[0] - Xb[0], Xa[1] - Xb[1], Xa[2] - Xb[2]));
    for (const [X, k, c] of [[Xa, B, cb], [Xb, A, ca]] as const) {
      const q = project(c, X);
      if (q) px.push(Math.hypot(q[0] - ((k.x + 0.5) * c.w) / p.w + 0.5, q[1] - ((k.y + 0.5) * c.h) / p.h + 0.5));
    }
  }
  if (!mm.length) return null;
  const med = (v: number[]) => v.sort((x, y) => x - y)[v.length >> 1];
  return { n: mm.length, px: med(px), mm: med(mm) };
}
